import React, { useState } from 'react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'Select Service',
    message: ''
  });

  const [submitStatus, setSubmitStatus] = useState('');

  const sanitizeEmail = (email) => {
    // Remove SQL injection patterns and dangerous characters
    // Allow valid email characters: letters, numbers, @, ., _, -, +
    let sanitized = email
      .replace(/['";\\]/g, '') // Remove quotes, semicolons, and backslashes (SQL injection chars)
      .replace(/--/g, '') // Remove SQL comment patterns
      .replace(/\/\*/g, '') // Remove SQL comment start
      .replace(/\*\//g, '') // Remove SQL comment end
      .replace(/<[^>]*>/g, '') // Remove any HTML tags (XSS prevention)
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+=/gi, ''); // Remove event handlers (onclick, onload, etc.)
    
    // Only allow valid email characters: letters, numbers, @, ., _, -, +
    sanitized = sanitized.replace(/[^a-zA-Z0-9@._+-]/g, '');
    
    return sanitized;
  };

  const sanitizeMessage = (message) => {
    // Remove SQL injection patterns and dangerous characters
    // Allow normal text, punctuation, and line breaks
    let sanitized = message
      .replace(/--/g, '') // Remove SQL comment patterns
      .replace(/\/\*/g, '') // Remove SQL comment start
      .replace(/\*\//g, '') // Remove SQL comment end
      .replace(/<script[^>]*>.*?<\/script>/gi, '') // Remove script tags
      .replace(/<[^>]*>/g, '') // Remove any HTML tags (XSS prevention)
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+\s*=/gi, '') // Remove event handlers (onclick, onload, etc.)
      .replace(/data:text\/html/gi, '') // Remove data URIs
      .replace(/vbscript:/gi, ''); // Remove vbscript protocol
    
    // Remove dangerous SQL patterns but keep normal quotes and punctuation
    // Replace single quotes with escaped version, remove semicolons that could be SQL injection
    sanitized = sanitized.replace(/;/g, ''); // Remove semicolons (SQL injection risk)
    
    return sanitized;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For phone field, only allow numbers and limit to 10 digits
    if (name === 'phone') {
      const numericValue = value.replace(/\D/g, ''); // Remove all non-digits
      const limitedValue = numericValue.slice(0, 10); // Limit to 10 digits
      setFormData(prevState => ({
        ...prevState,
        [name]: limitedValue
      }));
    } 
    // For name field, only allow letters and spaces
    else if (name === 'name') {
      const lettersOnly = value.replace(/[^a-zA-Z\s]/g, ''); // Remove all non-letters except spaces
      setFormData(prevState => ({
        ...prevState,
        [name]: lettersOnly
      }));
    }
    // For email field, sanitize to prevent SQL injection
    else if (name === 'email') {
      const sanitized = sanitizeEmail(value);
      setFormData(prevState => ({
        ...prevState,
        [name]: sanitized
      }));
    }
    // For message field, sanitize to prevent SQL injection and XSS
    else if (name === 'message') {
      const sanitized = sanitizeMessage(value);
      setFormData(prevState => ({
        ...prevState,
        [name]: sanitized
      }));
    }
    else {
      setFormData(prevState => ({
        ...prevState,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('Please wait...');

    // Sanitize email and message before submission
    const sanitizedFormData = {
      ...formData,
      email: sanitizeEmail(formData.email),
      message: sanitizeMessage(formData.message)
    };

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sanitizedFormData.email)) {
      setSubmitStatus('Please enter a valid email address');
      setTimeout(() => {
        setSubmitStatus('');
      }, 3000);
      return;
    }

    // Prepare the data for submission
    const submissionData = {
      access_key: 'b0006a1f-53c4-43f0-bdbf-6948469d473f',
      ...sanitizedFormData
    };

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json();
      
      if (response.status === 200) {
        setSubmitStatus('Message sent successfully!');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: 'Select Service',
          message: ''
        });
      } else {
        setSubmitStatus(result.message || 'Something went wrong!');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('Something went wrong!');
    }

    // Clear status message after 3 seconds
    setTimeout(() => {
      setSubmitStatus('');
    }, 3000);
  };

  return (
    <section className="contact" id="contact">
      <div className="container">
        <div className="section-title">
          <h2>Contact Us</h2>
          <p>Get in touch for a free consultation</p>
        </div>

        <div className="contact-container">
          <div className="contact-info">
            <div className="info-item">
              <h3>Contact Information</h3>
              <p>
                <i className="fas fa-phone"></i>
                <span>(386) 864-9197</span>
              </p>
              <p>
                <i className="fas fa-envelope"></i>
                <span>gardeniallc3@gmail.com</span>
              </p>
              <p>
                <i className="fas fa-map-marker-alt"></i>
                <span>PO Box 243, Seville, FL 32190</span>
              </p>
            </div>
            
            <div className="info-item">
              <h3>Business Hours</h3>
              <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
              <p>Saturday: 9:00 AM - 4:00 PM</p>
              <p>Sunday: Closed</p>
            </div>

            <div className="info-item">
              <h3>Service Areas</h3>
              <div className="service-regions">
                <div className="region">
                  <h4>Flagler County:</h4>
                  <p><strong>Palm Coast</strong>, <strong>Bunnell</strong>, <strong>Flagler Beach</strong></p>
                </div>
                <div className="region">
                  <h4>Volusia County:</h4>
                  <p><strong>Ormond Beach, Daytona Beach, and surrounding areas</strong></p>
                </div>
              </div>
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <input type="checkbox" name="botcheck" style={{ display: 'none' }} />
            
            <div className="form-group">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name (letters only)"
                pattern="[A-Za-z\s]+"
                required
              />
            </div>

            <div className="form-group">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email"
                pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                required
              />
            </div>

            <div className="form-group">
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Your Phone (10 digits)"
                maxLength={10}
                pattern="[0-9]{10}"
                inputMode="numeric"
                required
              />
            </div>

            <div className="form-group">
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
              >
                <option value="Select Service" disabled>Select Service</option>
                <option value="Commercial">Commercial Services</option>
                <option value="Residential">Residential Services</option>
                <option value="Landscape">Landscape Design</option>
                <option value="Maintenance">Landscape Maintenance</option>
                <option value="Irrigation">Irrigation Systems</option>
                <option value="Drainage">Drainage Solutions</option>
                <option value="Mulch">Mulch Installation</option>
                <option value="Stone">Stone Installation</option>
                <option value="Sod">Sod Installation</option>
                <option value="Grading">Grading Services</option>
                <option value="SmallTree">Small Tree Services</option>
                <option value="PalmTree">Palm Tree Services</option>
                <option value="BrushCleanup">Brush Clean-ups / Land Clearing</option>
                <option value="GrubControl">Grub Control</option>
                <option value="VegetationControl">Vegetation Control</option>
                <option value="Other">Other Services</option>
              </select>
            </div>

            <div className="form-group">
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Your Message"
                required
                rows="5"
              ></textarea>
            </div>

            <button type="submit" className="submit-btn">
              Send Message
            </button>

            {submitStatus && (
              <div className="submit-status" style={{
                marginTop: '1rem',
                textAlign: 'center',
                color: submitStatus.includes('success') ? '#4CAF50' : '#f44336'
              }}>
                {submitStatus}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact; 